import React, { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { Button } from '@winnin/winnin_ui.components.button'
import { Modal } from '@winnin/winnin_ui.components.modal'
import { Title } from '@winnin/winnin_ui.components.title'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { SMALL_MODAL } from 'consts/modalStyles'
import { useWinninAIReport } from 'contexts/WinninAIReportContext'
import { customerioTracker } from 'lib'
import { T, useTranslate } from 'lib/tolgee'
import { UserStore } from 'stores'
import { TOLGEE_GLOBALS, analyticsTracker } from 'utils'

import BrandAnalysisContent from './components/BrandAnalysisContent'
import CreatorsSearchContent from './components/CreatorsSearchContent'
import ErrorContent from './components/ErrorContent'
import { CREATE_CREATORS_SEARCH_REPORT, CREATE_TERRITORY_ANALYSIS_REPORT, WINNIN_AI_REPORT_WALLET_QUERY } from './query'

import './index.scss'

const REPORT_TYPES = {
  territory_analysis: BrandAnalysisContent,
  search_creators: CreatorsSearchContent,
}

const SUBMIT_RULES = {
  territory_analysis: ['brand', 'brandDescription'],
  search_creators: ['tier'],
}

const ALLOWED_TYPES = {
  territory_analysis: 'territory_analysis',
  search_creators: 'search_creators',
}

const TOLGEE_PREFIX = 'GenerateWinninAIReportUseCaseModal'

export default function GenerateWinninAIReportUseCaseModal() {
  const { winninAIReport, resetWinninAIReport, backToPreviousStep } = useWinninAIReport()
  const [formValues, setFormValues] = useState({})
  const { createToast } = useToast()
  const t = useTranslate()
  const history = useHistory()
  const shouldRenderUseCaseModal = ALLOWED_TYPES?.[winninAIReport?.reportType]
  const isWinner = UserStore?.userInfo?.company?.id === 'WINNIN'

  const { data, loading } = useQuery(WINNIN_AI_REPORT_WALLET_QUERY, {
    fetchPolicy: 'network-only',
    skip: !winninAIReport?.id,
  })

  function handleReportError(error) {
    if (error?.message === 'insufficient-funds') {
      createToast({
        content: t({
          key: `${TOLGEE_PREFIX}-insuficientFundsToast`,
          defaultValue:
            'Ops! Você não tem créditos disponíveis para criar esse Winnin Intelligence Report. Entre em contato com nossos consultores.',
        }),
      })
      return
    }

    createToast({
      content: t({
        key: `${TOLGEE_PREFIX}-errorToast`,
        defaultValue: 'Ops! Tivemos um erro na geração do seu Winnin Intelligence Report. Tente novamente.',
      }),
    })
  }

  function handleReportCompletion(report) {
    resetWinninAIReport()

    if (report?.success === false && report?.message === 'insufficient-funds') {
      createToast({
        content: t({
          key: `${TOLGEE_PREFIX}-insuficientFundsToast`,
          defaultValue:
            'Ops! Você não tem créditos disponíveis para criar esse Winnin Intelligence Report. Entre em contato com nossos consultores.',
        }),
      })
      return
    }

    if (report?.id && report?.workspaceId) {
      history.push(`/workspace/${report?.workspaceId}/jobs/${report?.id}/dashboard`)
    }
  }

  const [generateTerritoryAnalysisReport, { loading: territoryLoading }] = useMutation(
    CREATE_TERRITORY_ANALYSIS_REPORT,
    {
      onCompleted: ({ createAutomaticJobTerritoryAnalysis }) => {
        handleReportCompletion(createAutomaticJobTerritoryAnalysis)
      },
      onError: error => {
        console.error('Error @ CREATE_TERRITORY_ANALYSIS_REPORT >>>>>>', error)

        handleReportError(error)
      },
    }
  )

  const [generateCreatorsSearchReport, { loading: creatorsSearchLoading }] = useMutation(
    CREATE_CREATORS_SEARCH_REPORT,
    {
      onCompleted: ({ createAutomaticJobSearchCreators }) => {
        handleReportCompletion(createAutomaticJobSearchCreators)
      },
      onError: error => {
        console.error('Error @ CREATE_CREATORS_SEARCH_REPORT >>>>>>', error)

        handleReportError(error)
      },
    }
  )

  const blockGenerateReportButton = SUBMIT_RULES?.[winninAIReport?.reportType]?.some(
    rule => !formValues[rule] || formValues[rule]?.length === 0
  )

  const reportTypeMutations = {
    search_creators: generateCreatorsSearchReport,
    territory_analysis: generateTerritoryAnalysisReport,
  }

  async function handleGenerateReportButtonClick(ignoreCharge = false) {
    const outputProviders = winninAIReport?.reportType === 'search_creators' ? null : winninAIReport?.outputProviders
    const generateZaiReport = reportTypeMutations[winninAIReport?.reportType]

    const generatedReport = await generateZaiReport({
      variables: {
        data: {
          ...(ignoreCharge === true
            ? {
                chargeOptions: {
                  ignoreChargeOnCurrentWorkspace: true,
                },
              }
            : {}),
          ...(winninAIReport?.reportType === 'territory_analysis' && { dateRange: '12' }),
          territoryId: `${winninAIReport?.entity}.${winninAIReport?.id}`,
          ...(outputProviders ? { outputProviders } : {}),
          outputLanguage: winninAIReport?.outputLanguage,
          ...formValues,
        },
      },
    })

    const jobId =
      generatedReport?.data?.createAutomaticJobTerritoryAnalysis?.id ||
      generatedReport?.data?.createAutomaticJobSearchCreators?.id

    const { CREATED_REPORT } = customerioTracker.eventNames
    customerioTracker.track(CREATED_REPORT, {
      id: winninAIReport?.id,
      job_id: jobId,
      type: winninAIReport?.reportType,
    })

    analyticsTracker.eventTrack('AI Report Modal Generate Click', {
      id: winninAIReport?.id,
      entity: winninAIReport?.entity,
      type: winninAIReport?.reportType,
      ignoreCharge: !!ignoreCharge,
      jobId,
    })
  }

  function onRequestClose() {
    resetWinninAIReport()
  }

  const ReportTypeComponent = REPORT_TYPES[winninAIReport?.reportType] ?? ErrorContent

  const notEnoughCredits = data?.myWallet?.amount <= 0

  return (
    <Modal
      isOpen={
        winninAIReport?.id &&
        winninAIReport?.entity &&
        winninAIReport?.reportType &&
        winninAIReport?.outputLanguage &&
        shouldRenderUseCaseModal
      }
      onRequestClose={onRequestClose}
      appElement={document.body}
      style={SMALL_MODAL}
      className="GenerateWinninAIReportUseCaseModal"
    >
      <div className="GenerateWinninAIReportUseCaseModal-content">
        <Title tag="h3" weight="bold">
          <T keyName={`${TOLGEE_PREFIX}-modalTitle`}>Winnin Intelligence Report</T>
        </Title>
        <div className="GenerateWinninAIReportUseCaseModal-reportTypes">
          <ReportTypeComponent setFormValues={setFormValues} formValues={formValues} />
        </div>
      </div>
      <div className="GenerateWinninAIReportUseCaseModal-actions">
        <Button onClick={backToPreviousStep} appearance="link" variation="faded">
          <T keyName={TOLGEE_GLOBALS.back.key}>{TOLGEE_GLOBALS.back.defaultValue}</T>
        </Button>

        <div>
          <Button onClick={resetWinninAIReport} appearance="link" variation="faded">
            <T keyName={TOLGEE_GLOBALS.cancel.key}>{TOLGEE_GLOBALS.cancel.defaultValue}</T>
          </Button>
          <Button
            onClick={handleGenerateReportButtonClick}
            disabled={
              blockGenerateReportButton || territoryLoading || creatorsSearchLoading || notEnoughCredits || loading
            }
          >
            <T keyName={`${TOLGEE_PREFIX}-generateReport`}>Gerar report</T>
          </Button>
          {isWinner && (
            <Button
              onClick={() => handleGenerateReportButtonClick(true)}
              disabled={blockGenerateReportButton || territoryLoading || creatorsSearchLoading}
            >
              <T keyName={TOLGEE_GLOBALS.generateFreeReport.key}>{TOLGEE_GLOBALS.generateFreeReport.defaultValue}</T>
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
